<template>
  <div class="flex flex-col h-full">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-lg font-semibold text-gray-900">Alertas</h3>
      <button @click="cargarAlarmas" :disabled="cargando" class="p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50" title="Actualizar alertas">
        <svg :class="['w-4 h-4', cargando ? 'animate-spin' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
        </svg>
      </button>
    </div>

    <div v-if="cargando && alarmas.length === 0" class="space-y-2">
      <div v-for="n in 2" :key="n" class="animate-pulse">
        <div class="bg-gray-100 rounded-lg p-3 space-y-2">
          <div class="h-3 bg-gray-200 rounded w-3/4"></div>
          <div class="h-2 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3">
      <div class="flex items-start">
        <svg class="w-5 h-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <div class="flex-1">
          <p class="text-sm text-red-700 font-medium">Error cargando alarmas</p>
          <p class="text-xs text-red-600 mt-1">{{ error }}</p>
          <button @click="cargarAlarmas" class="text-xs text-red-600 hover:text-red-800 font-medium mt-2 underline">
            Reintentar
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="alarmas.length === 0" class="flex-1 flex flex-col items-center justify-center text-center py-8">
      <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
        <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
      </div>
      <p class="text-sm text-gray-600 font-medium">Todo en orden</p>
      <p class="text-xs text-gray-500 mt-1">No hay alertas activas</p>
    </div>

    <div v-else class="flex-1 overflow-y-auto space-y-2 pr-1">
      <div v-for="alarma in alarmas" :key="alarma.id" class="bg-white border rounded-lg overflow-hidden hover:shadow-md transition-shadow" :class="getClaseBordeAlarma(alarma.prioridad)">
        <div class="p-3">
          <div class="flex items-start justify-between mb-2">
            <div class="flex items-start flex-1 min-w-0">
              <div class="w-2 h-2 rounded-full mt-1.5 mr-2 flex-shrink-0" :class="getClasePuntoAlarma(alarma.prioridad)"></div>
              <div class="flex-1 min-w-0">
                <h4 class="text-sm font-semibold text-gray-900 truncate">
                  {{ alarma.titulo }}
                </h4>
                <p class="text-xs text-gray-600 line-clamp-2 mt-0.5">
                  {{ alarma.descripcion }}
                </p>
              </div>
            </div>

            <span class="ml-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide whitespace-nowrap flex-shrink-0" :class="getClaseBadgePrioridad(alarma.prioridad)">
              {{ obtenerTextoPrioridad(alarma.prioridad) }}
            </span>
          </div>

          <div class="flex items-center justify-between text-[10px] text-gray-500 mt-2">
            <div class="flex items-center">
              <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              {{ formatearFecha(alarma.fecha_creacion) }}
            </div>

            <span class="px-1.5 py-0.5 rounded text-[9px] font-medium uppercase" :class="getClaseEstado(alarma.estado)">
              {{ obtenerTextoEstado(alarma.estado) }}
            </span>
          </div>

          <button v-if="alarma.estado === 'pendiente'" @click="marcarComoResuelta(alarma.id)" :disabled="resolviendoAlarma === alarma.id" class="w-full mt-2 px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            {{ resolviendoAlarma === alarma.id ? 'Resolviendo...' : 'Marcar como resuelta' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="alarmas.length > 0" class="mt-3 pt-3 border-t border-gray-200">
      <p class="text-xs text-gray-500 text-center">
        {{ alarmas.length }} alerta{{ alarmas.length !== 1 ? 's' : '' }} activa{{ alarmas.length !== 1 ? 's' : '' }}
      </p>
    </div>
  </div>
</template>

<script>
import AlarmasService from '@/services/dispositivos/alarmas.js'

export default {
  name: 'AlertasDispositivo',

  props: {
    dispositivoId: {
      type: [Number, String],
      required: true
    },
    autoRefresh: {
      type: Boolean,
      default: false
    },
    refreshInterval: {
      type: Number,
      default: 30000 // 30 segundos
    }
  },

  data() {
    return {
      alarmas: [],
      cargando: false,
      error: null,
      resolviendoAlarma: null,
      refreshTimer: null
    }
  },

  mounted() {
    this.cargarAlarmas()

    if (this.autoRefresh) {
      this.iniciarAutoRefresh()
    }
  },

  beforeUnmount() {
    this.detenerAutoRefresh()
  },

  methods: {
    async cargarAlarmas() {
      if (this.cargando) return

      try {
        this.cargando = true
        this.error = null

        // FRONTEND FILTERING - obtiene alarmas del dispositivo
        this.alarmas = await AlarmasService.obtenerAlarmasDispositivo(
          this.dispositivoId,
          {
            estado: 'pendiente',  // Solo pendientes
            limit: 10  // Máximo 10 alertas
          }
        )

      } catch (error) {
        this.error = error.message || 'Error desconocido'
      } finally {
        this.cargando = false
      }
    },

    async marcarComoResuelta(alarmaId) {
      try {
        this.resolviendoAlarma = alarmaId

        await AlarmasService.actualizarEstadoAlarma(alarmaId, 'resuelto')

        // Recargar alarmas (el cache se invalida automáticamente)
        await this.cargarAlarmas()

      } catch (error) {
        this.error = 'Error al resolver la alarma'
      } finally {
        this.resolviendoAlarma = null
      }
    },

    iniciarAutoRefresh() {
      this.refreshTimer = setInterval(() => {
        // Invalidar cache para forzar recarga
        AlarmasService.invalidarCache()
        this.cargarAlarmas()
      }, this.refreshInterval)
    },

    detenerAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    // Métodos de estilo
    getClaseBordeAlarma(prioridad) {
      const clases = {
        'critica': 'border-l-4 border-red-500',
        'alta': 'border-l-4 border-orange-500',
        'media': 'border-l-4 border-yellow-500',
        'baja': 'border-l-4 border-blue-500'
      }
      return clases[prioridad] || 'border-l-4 border-gray-300'
    },

    getClasePuntoAlarma(prioridad) {
      const clases = {
        'critica': 'bg-red-500 animate-pulse',
        'alta': 'bg-orange-500',
        'media': 'bg-yellow-500',
        'baja': 'bg-blue-500'
      }
      return clases[prioridad] || 'bg-gray-400'
    },

    getClaseBadgePrioridad(prioridad) {
      const clases = {
        'critica': 'bg-red-100 text-red-800',
        'alta': 'bg-orange-100 text-orange-800',
        'media': 'bg-yellow-100 text-yellow-800',
        'baja': 'bg-blue-100 text-blue-800'
      }
      return clases[prioridad] || 'bg-gray-100 text-gray-800'
    },

    getClaseEstado(estado) {
      const clases = {
        'pendiente': 'bg-red-50 text-red-700',
        'en_proceso': 'bg-orange-50 text-orange-700',
        'resuelto': 'bg-green-50 text-green-700'
      }
      return clases[estado] || 'bg-gray-50 text-gray-700'
    },

    obtenerTextoPrioridad(prioridad) {
      const textos = {
        'critica': 'Crítica',
        'alta': 'Alta',
        'media': 'Media',
        'baja': 'Baja'
      }
      return textos[prioridad] || prioridad
    },

    obtenerTextoEstado(estado) {
      const textos = {
        'pendiente': 'Pendiente',
        'en_proceso': 'En Proceso',
        'resuelto': 'Resuelto'
      }
      return textos[estado] || estado
    },

    formatearFecha(fecha) {
      try {
        const date = new Date(fecha)
        const ahora = new Date()
        const diffMs = ahora - date
        const diffMins = Math.floor(diffMs / 60000)
        const diffHoras = Math.floor(diffMs / 3600000)
        const diffDias = Math.floor(diffMs / 86400000)

        if (diffMins < 1) return 'Ahora'
        if (diffMins < 60) return `Hace ${diffMins}m`
        if (diffHoras < 24) return `Hace ${diffHoras}h`
        if (diffDias === 1) return 'Ayer'
        if (diffDias < 7) return `Hace ${diffDias}d`

        return date.toLocaleDateString('es-CL', {
          day: '2-digit',
          month: 'short'
        })
      } catch (error) {
        return 'Fecha inválida'
      }
    }
  }
}
</script>

<style scoped>
/* Scrollbar personalizado */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>